/**
 * The start screen — how to play, and what you are trying to do.
 *
 * Kate never asked for this, because she already knew her own game. Anyone else opening
 * it does not, and the objective is the least obvious part precisely because she was so
 * clear about it:
 *
 *   "Well, actually, there's no winning the games... You just try to survive and like
 *    have fun being different animals."
 *
 * So the objective panel says exactly that, in her terms, rather than inventing a goal
 * she deliberately removed.
 */
const STORAGE_KEY = 'animal-attack:seen-start'

export class StartScreen {
  private readonly root: HTMLDivElement
  onStart: (() => void) | null = null
  /** Kate: "before you load into the game, you need to choose what level you want." */
  onDifficulty: ((level: 'easy' | 'normal' | 'hard') => void) | null = null
  difficulty: 'easy' | 'normal' | 'hard' = 'normal'

  constructor() {
    this.root = document.createElement('div')
    this.root.className = 'start'
    this.root.innerHTML = `
      <div class="start-panel">
        <h1>Animal Attack</h1>
        <p class="start-by">A game designed by Kate, age 9</p>

        <div class="start-goal">
          <h2>What are you trying to do?</h2>
          <p><strong>There is no winning.</strong> You survive, and you have fun being
          different animals. That is the whole game, and it is deliberate.</p>
        </div>

        <ol class="start-steps">
          <li><strong>You start as a human.</strong> You have 50 coupons.</li>
          <li><strong>Collect coupons</strong> by walking over them. They are scattered around the map.</li>
          <li><strong>Buy a morph tool</strong> — open <strong>MENU → Shop</strong> (or press
              <strong>P</strong>) and buy one. That is the only way to become an animal. Each one is different —
              a bunny is fast and hops high, a wolf can sniff out prey, a bird flies as high as the trees,
              a chameleon can hide.</li>
          <li><strong>Stay alive.</strong> Watch your hearts, your hunger and your thirst. Drink at the water.
              Do not run into trees.</li>
          <li><strong>Win competitions</strong> — a kill match, an obstacle course, or hide and seek.
              Winning one is the <em>only</em> way to get a squishy card.</li>
          <li><strong>Collect and trade squishy cards.</strong> The rarer the animal is in real life,
              the rarer the card.</li>
        </ol>

        <div class="start-controls">
          <div>
            <h3>On iPad or phone</h3>
            <p>Just like Minecraft: press the arrows on the <strong>D-pad</strong> to move, and
            double-tap forward to sprint. Drag anywhere else to look around.
            <strong>JUMP</strong>, <strong>SPRINT</strong> and <strong>SNEAK</strong> are the
            column on the right, with <strong>ATTACK</strong> beside them.
            The <strong>MENU</strong> button opens the shop, your cards, and everything else.</p>
          </div>
          <div>
            <h3>On a computer</h3>
            <p>Arrow keys or WASD to move, mouse to look, space to jump, shift to crouch.
            <strong>Click the screen</strong> to take mouse control, and press
            <strong>Esc</strong> to get your cursor back.</p>
            <p>Use the <strong>MENU</strong> button, top-left, for the shop and everything else —
            or the keys: <strong>P</strong> shop · <strong>C</strong> cards ·
            <strong>K</strong> competition · <strong>G</strong> dig · <strong>T</strong> chat ·
            <strong>O</strong> settings.</p>
          </div>
        </div>

        <div class="start-difficulty">
          <h3>Choose your difficulty</h3>
          <p class="start-difficulty-note">Offline, animals hunt you. Harder means they notice you
          from further away and hit harder.</p>
          <div class="start-difficulty-picker">
            <button data-difficulty="easy">Easy</button>
            <button data-difficulty="normal" class="is-selected">Normal</button>
            <button data-difficulty="hard">Hard</button>
          </div>
        </div>

        <button class="start-play">Play</button>
      </div>
    `
    for (const button of this.root.querySelectorAll<HTMLButtonElement>('[data-difficulty]')) {
      button.addEventListener('click', () => {
        for (const other of this.root.querySelectorAll('[data-difficulty]')) {
          other.classList.toggle('is-selected', other === button)
        }
        this.difficulty = button.dataset.difficulty as 'easy' | 'normal' | 'hard'
        this.onDifficulty?.(this.difficulty)
      })
    }

    const play = this.root.querySelector<HTMLButtonElement>('.start-play')!
    play.addEventListener('click', () => this.dismiss())
    document.body.append(this.root)
  }

  /** Shown on a first visit; afterwards it is opened from the menu. */
  static seenBefore(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'yes'
    } catch {
      return false
    }
  }

  get isOpen(): boolean {
    return !this.root.hidden
  }

  /** Hide it without starting the game --- used when another screen takes over. */
  close(): void {
    this.root.hidden = true
  }

  open(): void {
    this.root.hidden = false
  }

  private dismiss(): void {
    this.root.hidden = true
    try {
      localStorage.setItem(STORAGE_KEY, 'yes')
    } catch {
      // Private browsing; it will simply show again next time.
    }
    this.onStart?.()
  }
}
